import { useState, useEffect } from 'react';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { useBoardContext } from '@/contexts/BoardContext';
import { CardPresenceProvider } from '@/contexts/OptimizedCardPresenceContext';
import { BoardPresenceProvider, useBoardPresence } from '@/contexts/BoardPresenceContext';
import { DragStateProvider } from '@/contexts/DragStateContext';
import { MobileColumnView } from './MobileColumnView';
import { MobileBottomNavigation } from './MobileBottomNavigation';
import { MobileCardCreator } from './MobileCardCreator';
import { MobileHeader } from './MobileHeader';
import { MobileFloatingActionButton } from './MobileFloatingActionButton';
import { MobileDashboard } from './MobileDashboard';
import { useToast } from '@/hooks/use-toast';
import { useScreenSize } from '@/hooks/useScreenSize';
import confetti from 'canvas-confetti';
import { useMemo } from 'react';

const MobileKanbanBoardContent = () => {
  const { 
    columns, 
    cards, 
    updateCardPosition, 
    currentBoardId,
    columnsLoading,
    cardsLoading,
    getTagName,
    showArchivedCards,
    setShowArchivedCards 
  } = useBoardContext();
  
  const { toast } = useToast();
  const { isSmallPhone, isTablet, width } = useScreenSize();
  const [currentColumnIndex, setCurrentColumnIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showCardCreator, setShowCardCreator] = useState(false);
  const [isOverviewMode, setIsOverviewMode] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);

  // Enhanced filtered cards with proper memoization
  const filteredCards = useMemo(() => {
    if (!cards) return [];
    
    if (!searchQuery.trim()) {
      return cards;
    }
    
    const searchLower = searchQuery.toLowerCase();
    
    return cards.filter(card => {
      if (card.title?.toLowerCase().includes(searchLower)) return true;
      if (card.description?.toLowerCase().includes(searchLower)) return true;
      if (card.category?.toLowerCase().includes(searchLower)) return true;
      
      if (card.tags && Array.isArray(card.tags)) {
        return card.tags.some(tagId => {
          const tagName = getTagName(tagId);
          return tagName.toLowerCase().includes(searchLower);
        });
      }
      
      return false;
    });
  }, [cards, searchQuery, getTagName]);

  useEffect(() => {
    setSearchQuery('');
    setCurrentColumnIndex(0);
  }, [currentBoardId]);

  // Enhanced wheel event handler for desktop mouse scroll support
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      const target = e.target as Element;
      
      // Don't interfere with dropdown menus or modals
      if (target.closest('.dropdown-menu') || target.closest('.context-menu') || target.closest('[role="dialog"]')) {
        return;
      }

      // Find the nearest scrollable container
      const scrollContainer = target.closest('.mobile-column-content') || 
                             target.closest('[data-radix-scroll-area-viewport]') ||
                             target.closest('.overflow-y-auto');
      
      if (scrollContainer) {
        // Allow natural wheel scrolling in scrollable containers
        const element = scrollContainer as HTMLElement;
        const canScrollDown = element.scrollTop < (element.scrollHeight - element.clientHeight);
        const canScrollUp = element.scrollTop > 0;
        
        // Only prevent default if we can't scroll in the intended direction
        if ((e.deltaY > 0 && !canScrollDown) || (e.deltaY < 0 && !canScrollUp)) {
          e.preventDefault();
        }
        
        return;
      }

      // For non-scrollable areas, allow natural behavior
    };

    // Use non-passive listener to allow preventDefault when needed
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const sourceColumn = columns?.find(col => col.id === source.droppableId);
    const destColumn = columns?.find(col => col.id === destination.droppableId);

    if (!sourceColumn || !destColumn) return;

    // Completion celebration
    if (destColumn.is_completed_column && !sourceColumn.is_completed_column) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6']
      });
      
      toast({
        title: "🎉 Task Completed!",
        description: "Great job finishing this task!"
      });
    }

    updateCardPosition({
      cardId: draggableId,
      newColumnId: destination.droppableId,
      newPosition: destination.index
    });
  };

  if (showDashboard) {
    return <MobileDashboard onClose={() => setShowDashboard(false)} />;
  }

  if (!currentBoardId || columnsLoading || cardsLoading || !columns) {
    return (
      <div className="h-[100dvh] flex flex-col bg-background">
        <div className="flex justify-center items-center flex-1">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const currentColumn = columns[currentColumnIndex];
  const currentColumnCards = filteredCards?.filter(card => 
    card.column_id === currentColumn?.id && (showArchivedCards || !card.archived)
  ) || [];

  // Responsive column width calculation
  const getColumnWidth = () => {
    if (isSmallPhone) return 240;
    if (isTablet) return 320;
    return 280;
  };

  const columnWidth = getColumnWidth();
  const overviewPadding = isSmallPhone ? 'p-2' : isTablet ? 'p-6' : 'p-4';
  const overviewGap = isSmallPhone ? 'gap-2' : isTablet ? 'gap-6' : 'gap-4';

  return (
    <div className="h-[100dvh] flex flex-col bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
      {/* Mobile Header */}
      <MobileHeader
        currentColumn={isOverviewMode ? undefined : currentColumn}
        filteredCards={isOverviewMode ? filteredCards : currentColumnCards}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        showSearch={showSearch}
        onSearchToggle={() => setShowSearch(!showSearch)}
        isOverviewMode={isOverviewMode}
        onOverviewToggle={() => setIsOverviewMode(!isOverviewMode)}
        showArchivedCards={showArchivedCards}
        onArchivedToggle={() => setShowArchivedCards(!showArchivedCards)}
        onCardCreate={() => setShowCardCreator(true)}
      />

      {/* Column Indicators */}
      {columns.length > 1 && !isOverviewMode && (
        <div className={`flex-shrink-0 flex justify-center gap-2 ${isSmallPhone ? 'py-1' : 'py-2'} bg-background/50`}>
          {columns.map((_, index) => (
            <button
              key={index}
              className={`h-2 w-2 rounded-full transition-all min-h-touch-target-sm min-w-touch-target-sm ${
                index === currentColumnIndex 
                  ? `bg-primary ${isSmallPhone ? 'w-4' : 'w-6'}` 
                  : 'bg-muted-foreground/30'
              }`}
              onClick={() => setCurrentColumnIndex(index)}
            />
          ))}
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 relative overflow-hidden">
        <DragDropContext onDragEnd={handleDragEnd}>
          {isOverviewMode ? (
            <div className="h-full overflow-x-auto overflow-y-hidden">
              <div 
                className={`flex ${overviewGap} ${overviewPadding} h-full`} 
                style={{ width: `${columns.length * (columnWidth + (isSmallPhone ? 8 : isTablet ? 24 : 16))}px` }}
              >
                {columns.map((column, index) => {
                  const columnCards = filteredCards?.filter(card => 
                    card.column_id === column.id && (showArchivedCards || !card.archived)
                  ) || [];
                  
                  return (
                    <div 
                      key={column.id} 
                      className="bg-background rounded-lg border border-border flex-shrink-0 cursor-pointer hover:shadow-md transition-shadow"
                      style={{ width: `${columnWidth}px` }}
                      onClick={() => {
                        setCurrentColumnIndex(index);
                        setIsOverviewMode(false);
                      }}
                    >
                      <div className={`border-b border-border ${isSmallPhone ? 'p-2' : 'p-3'}`}>
                        <h3 className={`font-semibold truncate ${isSmallPhone ? 'text-xs' : 'text-sm'}`}>{column.title}</h3>
                        <p className={`text-muted-foreground ${isSmallPhone ? 'text-xs' : 'text-xs'}`}>{columnCards.length} cards</p>
                      </div>
                      <div className={`space-y-2 overflow-y-auto ${isSmallPhone ? 'p-1 max-h-32' : 'p-2 max-h-48'}`}>
                        {columnCards.slice(0, 3).map((card) => (
                          <div key={card.id} className={`bg-card p-2 rounded border ${isSmallPhone ? 'text-xs' : 'text-xs'}`}>
                            <p className="font-medium truncate">{card.title}</p>
                            {card.description && !isSmallPhone && (
                              <p className="text-muted-foreground truncate">{card.description}</p>
                            )}
                          </div>
                        ))}
                        {columnCards.length > 3 && (
                          <div className={`text-center text-muted-foreground ${isSmallPhone ? 'text-xs' : 'text-xs'}`}>
                            +{columnCards.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <MobileColumnView
              columns={columns}
              currentColumnIndex={currentColumnIndex}
              onColumnChange={setCurrentColumnIndex}
              cards={currentColumnCards}
              filteredCards={filteredCards}
              showArchivedCards={showArchivedCards}
            />
          )}
        </DragDropContext>
      </div>

      {/* Bottom Navigation */}
      {!isOverviewMode && (
        <MobileBottomNavigation
          columns={columns}
          currentColumnIndex={currentColumnIndex}
          onColumnChange={setCurrentColumnIndex}
          currentColumn={currentColumn}
        />
      )}

      {/* Floating Action Button */}
      <MobileFloatingActionButton
        onCardCreate={() => setShowCardCreator(true)}
        onSearch={() => setShowSearch(true)}
        onOverview={() => setIsOverviewMode(!isOverviewMode)}
      />

      {/* Mobile Card Creator */}
      <MobileCardCreator
        open={showCardCreator}
        onOpenChange={setShowCardCreator}
        columnId={currentColumn?.id || ''}
      />
    </div>
  );
};

const MobileKanbanBoard = () => {
  const { currentBoardId } = useBoardContext();

  if (!currentBoardId) {
    return <div>No board selected</div>;
  }

    return (
   <BoardPresenceProvider boardId={currentBoardId}>
    <CardPresenceProvider boardId={currentBoardId}>
      <DragStateProvider boardId={currentBoardId}>
        <MobileKanbanBoardContent />
      </DragStateProvider>
      </CardPresenceProvider>
   </BoardPresenceProvider>
  );
};

export default MobileKanbanBoard;
